import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Save, AlertCircle, Info, Building2, Globe, Pencil, Check, Calendar, Clock, AlertTriangle, Settings } from 'lucide-react';
import examScheduleService, { ExamConfig } from '../../services/examScheduleService';
import { calendarSettingsService } from '../../services/calendarSettingsService';
import { fetchAcademicYears, AcademicYear } from '../../../../services/academicPlanningService';
import { fetchCampuses, Campus } from '../../../../services/etablissementService';

type ExamPeriodType = 'SINGLE' | 'PER_SEMESTER';

interface SemesterDates {
  semester1Start: string | null;
  semester1End: string | null;
  semester2Start: string | null;
  semester2End: string | null;
}

interface ExamConfigurationSettingsProps {
  canManage?: boolean;
}

export const ExamConfigurationSettings: React.FC<ExamConfigurationSettingsProps> = ({ canManage = true }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Academic years and campuses
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string | null>(null);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);
  const [semesterDates, setSemesterDates] = useState<SemesterDates>({
    semester1Start: null,
    semester1End: null,
    semester2Start: null,
    semester2End: null,
  });

  // Configuration data
  const [, setConfigId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    // Campus scope
    isUnifiedForAllCampuses: true,
    // Exam period configuration
    examPeriodType: 'PER_SEMESTER' as ExamPeriodType,
    examPeriod1Start: '',
    examPeriod1End: '',
    examPeriod2Start: '',
    examPeriod2End: '',
    // Core settings
    maxExamsPerDayPerGroup: 2,
    minDaysBetweenExams: 0,
    enforceExamPeriod: true,
    allowWeekendExams: true,
    // Rattrapage settings
    rattrapageEnabled: true,
    rattrapagePeriodType: 'PER_SEMESTER' as ExamPeriodType,
    rattrapagePeriod1Start: '',
    rattrapagePeriod1End: '',
    rattrapagePeriod2Start: '',
    rattrapagePeriod2End: '',
  });
  const [originalData, setOriginalData] = useState(formData);

  // Helper function to convert day/month from template to full date
  const convertTemplateToDate = (
    day: number | undefined,
    month: number | undefined,
    academicYearStartDate: string,
    academicYearStartMonth: number
  ): string | null => {
    if (!day || !month) return null;

    // Parse the academic year start date to get the year
    const startDate = new Date(academicYearStartDate);
    const startYear = startDate.getFullYear();

    // If the month is before the academic year start month, it's in the next calendar year
    const year = month < academicYearStartMonth ? startYear + 1 : startYear;

    // Format as YYYY-MM-DD
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
  };

  // Load academic years, campuses, and template
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const token = localStorage.getItem('nexedu_access_token');
        if (!token) return;

        const [yearsData, campusList, template] = await Promise.all([
          fetchAcademicYears(token),
          fetchCampuses(token),
          calendarSettingsService.getAcademicYearTemplate(),
        ]);

        setAcademicYears(yearsData);
        setCampuses(campusList);
        setHasMultipleCampuses(campusList.length > 1);

        // Set current academic year as default
        const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearId(currentYear.id);

          // The "Année académique" template is the source of truth the user edits, so
          // it takes precedence; fall back to any per-year dates stored on the AcademicYear.
          const academicYearStartMonth = template?.startMonth || 9; // Default September

          const sem1Start = convertTemplateToDate(template?.semester1StartDay, template?.semester1StartMonth, currentYear.startDate, academicYearStartMonth)
            || currentYear.semester1StartDate?.split('T')[0] || null;
          const sem1End = convertTemplateToDate(template?.semester1EndDay, template?.semester1EndMonth, currentYear.startDate, academicYearStartMonth)
            || currentYear.semester1EndDate?.split('T')[0] || null;
          const sem2Start = convertTemplateToDate(template?.semester2StartDay, template?.semester2StartMonth, currentYear.startDate, academicYearStartMonth)
            || currentYear.semester2StartDate?.split('T')[0] || null;
          const sem2End = convertTemplateToDate(template?.semester2EndDay, template?.semester2EndMonth, currentYear.startDate, academicYearStartMonth)
            || currentYear.semester2EndDate?.split('T')[0] || null;

          setSemesterDates({
            semester1Start: sem1Start,
            semester1End: sem1End,
            semester2Start: sem2Start,
            semester2End: sem2End,
          });
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };

    loadInitialData();
  }, []);

  // Update semester dates when academic year changes
  useEffect(() => {
    const updateSemesterDates = async () => {
      if (!selectedAcademicYearId) return;

      const selectedYear = academicYears.find(y => y.id === selectedAcademicYearId);
      if (!selectedYear) return;

      try {
        const template = await calendarSettingsService.getAcademicYearTemplate();
        const academicYearStartMonth = template?.startMonth || 9;

        // Template (edited in the "Année académique" tab) takes precedence over any
        // per-year dates stored on the AcademicYear record.
        const sem1Start = convertTemplateToDate(template?.semester1StartDay, template?.semester1StartMonth, selectedYear.startDate, academicYearStartMonth)
          || selectedYear.semester1StartDate?.split('T')[0] || null;
        const sem1End = convertTemplateToDate(template?.semester1EndDay, template?.semester1EndMonth, selectedYear.startDate, academicYearStartMonth)
          || selectedYear.semester1EndDate?.split('T')[0] || null;
        const sem2Start = convertTemplateToDate(template?.semester2StartDay, template?.semester2StartMonth, selectedYear.startDate, academicYearStartMonth)
          || selectedYear.semester2StartDate?.split('T')[0] || null;
        const sem2End = convertTemplateToDate(template?.semester2EndDay, template?.semester2EndMonth, selectedYear.startDate, academicYearStartMonth)
          || selectedYear.semester2EndDate?.split('T')[0] || null;

        setSemesterDates({
          semester1Start: sem1Start,
          semester1End: sem1End,
          semester2Start: sem2Start,
          semester2End: sem2End,
        });
      } catch (err) {
        console.error('Failed to load template:', err);
        // Fall back to academic year dates only
        setSemesterDates({
          semester1Start: selectedYear.semester1StartDate?.split('T')[0] || null,
          semester1End: selectedYear.semester1EndDate?.split('T')[0] || null,
          semester2Start: selectedYear.semester2StartDate?.split('T')[0] || null,
          semester2End: selectedYear.semester2EndDate?.split('T')[0] || null,
        });
      }
    };

    updateSemesterDates();
  }, [selectedAcademicYearId, academicYears]);

  // Load config when academic year or campus changes
  useEffect(() => {
    const loadData = async () => {
      // Wait for campuses to be loaded before proceeding
      if (!selectedAcademicYearId || campuses.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // First, load the base config to determine if it's unified or per-campus
        // This is needed on initial load when selectedCampusId is null
        const baseConfig = await examScheduleService.getConfig(selectedAcademicYearId);
        console.log('[ExamConfig] Base config loaded:', baseConfig);

        // If the config says it's NOT unified (per-campus), we need special handling
        const isPerCampus = baseConfig && baseConfig.isUnifiedForAllCampuses === false;
        console.log('[ExamConfig] isPerCampus:', isPerCampus, 'selectedCampusId:', selectedCampusId, 'hasMultipleCampuses:', hasMultipleCampuses);

        let configToUse = baseConfig;

        if (isPerCampus && !selectedCampusId && hasMultipleCampuses) {
          // Per-campus mode but no campus selected yet - select the first one
          console.log('[ExamConfig] Auto-selecting first campus:', campuses[0].id);
          setSelectedCampusId(campuses[0].id);
          // Load will be triggered again by the state change
          setLoading(false);
          return;
        } else if (isPerCampus && selectedCampusId) {
          // Load the campus-specific config
          console.log('[ExamConfig] Loading campus-specific config for:', selectedCampusId);
          configToUse = await examScheduleService.getConfig(selectedAcademicYearId, selectedCampusId);
          console.log('[ExamConfig] Campus config loaded:', configToUse);
        }

        if (configToUse) {
          setConfigId(configToUse.id);
          const loadedData = {
            isUnifiedForAllCampuses: configToUse.isUnifiedForAllCampuses ?? true,
            examPeriodType: (configToUse.examPeriodType || 'PER_SEMESTER') as ExamPeriodType,
            examPeriod1Start: configToUse.examPeriod1Start ? configToUse.examPeriod1Start.split('T')[0] : '',
            examPeriod1End: configToUse.examPeriod1End ? configToUse.examPeriod1End.split('T')[0] : '',
            examPeriod2Start: configToUse.examPeriod2Start ? configToUse.examPeriod2Start.split('T')[0] : '',
            examPeriod2End: configToUse.examPeriod2End ? configToUse.examPeriod2End.split('T')[0] : '',
            maxExamsPerDayPerGroup: configToUse.maxExamsPerDayPerGroup,
            minDaysBetweenExams: configToUse.minDaysBetweenExams,
            enforceExamPeriod: configToUse.enforceExamPeriod,
            allowWeekendExams: configToUse.allowWeekendExams,
            rattrapageEnabled: configToUse.rattrapageEnabled,
            rattrapagePeriodType: (configToUse.rattrapagePeriodType || 'PER_SEMESTER') as ExamPeriodType,
            rattrapagePeriod1Start: configToUse.rattrapagePeriod1Start ? configToUse.rattrapagePeriod1Start.split('T')[0] : '',
            rattrapagePeriod1End: configToUse.rattrapagePeriod1End ? configToUse.rattrapagePeriod1End.split('T')[0] : '',
            rattrapagePeriod2Start: configToUse.rattrapagePeriod2Start ? configToUse.rattrapagePeriod2Start.split('T')[0] : '',
            rattrapagePeriod2End: configToUse.rattrapagePeriod2End ? configToUse.rattrapagePeriod2End.split('T')[0] : '',
          };
          console.log('[ExamConfig] Setting form data:', loadedData);
          setFormData(loadedData);
          setOriginalData(loadedData);
        }
      } catch (err) {
        console.error('Failed to load exam configuration:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedAcademicYearId, selectedCampusId, hasMultipleCampuses, campuses]);

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Validation for exam period dates within semester boundaries
  const getDateValidation = (field: string, value: string) => {
    if (!value) return null;

    const date = new Date(value);
    const s1End = semesterDates.semester1End ? new Date(semesterDates.semester1End) : null;
    const s2End = semesterDates.semester2End ? new Date(semesterDates.semester2End) : null;
    const s1Start = semesterDates.semester1Start ? new Date(semesterDates.semester1Start) : null;
    const s2Start = semesterDates.semester2Start ? new Date(semesterDates.semester2Start) : null;

    // For exam period 1 (semester 1) - validate only if S1 dates exist
    if (field === 'examPeriod1Start' || field === 'examPeriod1End') {
      if (s1Start && date < s1Start) {
        return `La date ne peut pas être avant le début du semestre 1 (${formatDate(semesterDates.semester1Start)})`;
      }
      if (s1End && date > s1End) {
        return `La date ne peut pas dépasser la fin du semestre 1 (${formatDate(semesterDates.semester1End)})`;
      }
    }

    // For exam period 2 (semester 2) - validate only if S2 dates exist
    if (field === 'examPeriod2Start' || field === 'examPeriod2End') {
      if (s2Start && date < s2Start) {
        return `La date ne peut pas être avant le début du semestre 2 (${formatDate(semesterDates.semester2Start)})`;
      }
      if (s2End && date > s2End) {
        return `La date ne peut pas dépasser la fin du semestre 2 (${formatDate(semesterDates.semester2End)})`;
      }
    }

    return null;
  };

  // Get max date for exam period date inputs
  const getMaxDateForField = (field: string): string | undefined => {
    if (field === 'examPeriod1Start' || field === 'examPeriod1End') {
      return semesterDates.semester1End || undefined;
    }
    if (field === 'examPeriod2Start' || field === 'examPeriod2End') {
      return semesterDates.semester2End || undefined;
    }
    return undefined;
  };

  // Get min date for exam period date inputs
  const getMinDateForField = (field: string): string | undefined => {
    if (field === 'examPeriod1Start' || field === 'examPeriod1End') {
      return semesterDates.semester1Start || undefined;
    }
    if (field === 'examPeriod2Start' || field === 'examPeriod2End') {
      return semesterDates.semester2Start || undefined;
    }
    return undefined;
  };

  const handleStartEditing = () => {
    setOriginalData({ ...formData });
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setFormData(originalData);
    setIsEditing(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || saving) return;

    // Validate exam period dates are within semester boundaries
    if (formData.examPeriodType === 'PER_SEMESTER') {
      const period1StartError = getDateValidation('examPeriod1Start', formData.examPeriod1Start);
      const period1EndError = getDateValidation('examPeriod1End', formData.examPeriod1End);
      const period2StartError = getDateValidation('examPeriod2Start', formData.examPeriod2Start);
      const period2EndError = getDateValidation('examPeriod2End', formData.examPeriod2End);

      const validationError = period1StartError || period1EndError || period2StartError || period2EndError;
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      const dataToSave: Partial<ExamConfig> = {
        academicYearId: selectedAcademicYearId,
        campusId: formData.isUnifiedForAllCampuses ? null : selectedCampusId,
        isUnifiedForAllCampuses: formData.isUnifiedForAllCampuses,
        examPeriodType: formData.examPeriodType,
        examPeriod1Start: formData.examPeriod1Start || null,
        examPeriod1End: formData.examPeriod1End || null,
        examPeriod2Start: formData.examPeriodType === 'PER_SEMESTER' ? (formData.examPeriod2Start || null) : null,
        examPeriod2End: formData.examPeriodType === 'PER_SEMESTER' ? (formData.examPeriod2End || null) : null,
        maxExamsPerDayPerGroup: formData.maxExamsPerDayPerGroup,
        minDaysBetweenExams: formData.minDaysBetweenExams,
        enforceExamPeriod: formData.enforceExamPeriod,
        allowWeekendExams: formData.allowWeekendExams,
        rattrapageEnabled: formData.rattrapageEnabled,
        rattrapagePeriodType: formData.rattrapagePeriodType,
        rattrapagePeriod1Start: formData.rattrapageEnabled ? (formData.rattrapagePeriod1Start || null) : null,
        rattrapagePeriod1End: formData.rattrapageEnabled ? (formData.rattrapagePeriod1End || null) : null,
        rattrapagePeriod2Start: formData.rattrapageEnabled && formData.rattrapagePeriodType === 'PER_SEMESTER' ? (formData.rattrapagePeriod2Start || null) : null,
        rattrapagePeriod2End: formData.rattrapageEnabled && formData.rattrapagePeriodType === 'PER_SEMESTER' ? (formData.rattrapagePeriod2End || null) : null,
      };

      const result = await examScheduleService.updateConfig(dataToSave);
      setConfigId(result.id);
      setOriginalData({ ...formData });
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Non définie';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Header with Edit/Save Button - only shown if user can manage */}
      {canManage && (
        <div className="flex justify-end">
          {!isEditing ? (
            <button
              type="button"
              onClick={handleStartEditing}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={20} />
              Modifier
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelEditing}
                className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!hasChanges || saving}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                  !hasChanges || saving
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save size={20} />
                )}
                Enregistrer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check size={20} />
          <span>Configuration enregistrée avec succès</span>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Settings className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-900">Configuration des Examens</h4>
            <p className="text-sm text-blue-700 mt-1">
              Configurez les règles de planification des examens: nombre maximum d'examens par jour,
              périodes d'examens, et options de rattrapage.
            </p>
          </div>
        </div>
      </div>

      {/* Campus Scope Settings */}
      {hasMultipleCampuses && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Building2 className="text-indigo-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Portée des paramètres</h3>
              <p className="text-sm text-gray-500">
                Choisissez si les paramètres sont unifiés pour tous les campus ou spécifiques à chaque campus
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {!isEditing ? (
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  formData.isUnifiedForAllCampuses
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <Globe size={18} />
                  <span className="font-medium">Unifié pour tous les campus</span>
                </div>
                {!formData.isUnifiedForAllCampuses && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-100 text-indigo-700 border-2 border-indigo-300">
                    <Building2 size={18} />
                    <span className="font-medium">Par campus</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-4">
                <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                  formData.isUnifiedForAllCampuses
                    ? 'bg-indigo-100 border-2 border-indigo-400'
                    : 'bg-white border-2 border-gray-200 hover:border-indigo-200'
                }`}>
                  <input
                    type="radio"
                    name="campusScope"
                    checked={formData.isUnifiedForAllCampuses}
                    onChange={() => {
                      updateField('isUnifiedForAllCampuses', true);
                      setSelectedCampusId(null);
                    }}
                    className="sr-only"
                  />
                  <Globe size={20} className={formData.isUnifiedForAllCampuses ? 'text-indigo-600' : 'text-gray-400'} />
                  <div>
                    <p className={`font-medium ${formData.isUnifiedForAllCampuses ? 'text-indigo-700' : 'text-gray-700'}`}>
                      Unifié pour tous les campus
                    </p>
                    <p className="text-xs text-gray-500">Les mêmes paramètres s'appliquent à tous les campus</p>
                  </div>
                </label>

                <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                  !formData.isUnifiedForAllCampuses
                    ? 'bg-indigo-100 border-2 border-indigo-400'
                    : 'bg-white border-2 border-gray-200 hover:border-indigo-200'
                }`}>
                  <input
                    type="radio"
                    name="campusScope"
                    checked={!formData.isUnifiedForAllCampuses}
                    onChange={() => {
                      updateField('isUnifiedForAllCampuses', false);
                      // Auto-select first campus when switching to per-campus mode
                      if (campuses.length > 0 && !selectedCampusId) {
                        setSelectedCampusId(campuses[0].id);
                      }
                    }}
                    className="sr-only"
                  />
                  <Building2 size={20} className={!formData.isUnifiedForAllCampuses ? 'text-indigo-600' : 'text-gray-400'} />
                  <div>
                    <p className={`font-medium ${!formData.isUnifiedForAllCampuses ? 'text-indigo-700' : 'text-gray-700'}`}>
                      Par campus
                    </p>
                    <p className="text-xs text-gray-500">Chaque campus a ses propres paramètres</p>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Academic Year and Campus Selection */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Calendar className="text-purple-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Sélection du contexte</h3>
            <p className="text-sm text-gray-500">
              Choisissez l'année académique {!formData.isUnifiedForAllCampuses && hasMultipleCampuses ? 'et le campus ' : ''}pour configurer
            </p>
          </div>
        </div>

        <div className={`grid ${!formData.isUnifiedForAllCampuses && hasMultipleCampuses ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Année académique
            </label>
            <select
              value={selectedAcademicYearId || ''}
              onChange={(e) => setSelectedAcademicYearId(e.target.value || null)}
              className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
            >
              <option value="">Sélectionner une année</option>
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>
          </div>

          {!formData.isUnifiedForAllCampuses && hasMultipleCampuses && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campus
              </label>
              <select
                value={selectedCampusId || ''}
                onChange={(e) => setSelectedCampusId(e.target.value || null)}
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">Sélectionner un campus</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Exam Period Configuration */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="text-blue-600" size={20} />
          Configuration des périodes d'examens
        </h3>

        {/* Exam Period Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Combien de périodes d'examens avez-vous ?
          </label>
          {!isEditing ? (
            <div className="flex items-center gap-2">
              <span className={`px-4 py-2 rounded-lg font-medium ${
                formData.examPeriodType === 'SINGLE'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {formData.examPeriodType === 'SINGLE'
                  ? 'Une seule période (jusqu\'à la fin de l\'année)'
                  : 'Deux périodes (une par semestre)'
                }
              </span>
            </div>
          ) : (
            <div className="flex gap-4">
              <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                formData.examPeriodType === 'SINGLE'
                  ? 'bg-blue-100 border-2 border-blue-400'
                  : 'bg-white border-2 border-gray-200 hover:border-blue-200'
              }`}>
                <input
                  type="radio"
                  name="examPeriodType"
                  checked={formData.examPeriodType === 'SINGLE'}
                  onChange={() => updateField('examPeriodType', 'SINGLE')}
                  className="sr-only"
                />
                <div>
                  <p className={`font-medium ${formData.examPeriodType === 'SINGLE' ? 'text-blue-700' : 'text-gray-700'}`}>
                    Une seule période
                  </p>
                  <p className="text-xs text-gray-500">Examens jusqu'à la fin de l'année</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                formData.examPeriodType === 'PER_SEMESTER'
                  ? 'bg-blue-100 border-2 border-blue-400'
                  : 'bg-white border-2 border-gray-200 hover:border-blue-200'
              }`}>
                <input
                  type="radio"
                  name="examPeriodType"
                  checked={formData.examPeriodType === 'PER_SEMESTER'}
                  onChange={() => updateField('examPeriodType', 'PER_SEMESTER')}
                  className="sr-only"
                />
                <div>
                  <p className={`font-medium ${formData.examPeriodType === 'PER_SEMESTER' ? 'text-blue-700' : 'text-gray-700'}`}>
                    Par semestre
                  </p>
                  <p className="text-xs text-gray-500">Une période d'examens par semestre</p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Semester Boundaries Info */}
        {formData.examPeriodType === 'PER_SEMESTER' && (
          <>
            {(semesterDates.semester1End || semesterDates.semester2End) ? (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Les examens doivent respecter les limites des semestres</p>
                    <p className="text-amber-700 mt-1">
                      {semesterDates.semester1End && `Semestre 1: ${formatDate(semesterDates.semester1Start)} au ${formatDate(semesterDates.semester1End)}`}
                      {semesterDates.semester1End && semesterDates.semester2End && ' | '}
                      {semesterDates.semester2End && `Semestre 2: ${formatDate(semesterDates.semester2Start)} au ${formatDate(semesterDates.semester2End)}`}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Dates des semestres non configurées</p>
                    <p className="text-red-700 mt-1">
                      Les dates des semestres ne sont pas configurées pour cette année académique.
                      Veuillez d'abord configurer les dates dans les paramètres de l'année académique pour activer la validation des périodes d'examens.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Exam Period Dates */}
        <div className="space-y-4">
          {/* Period 1 */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">
              {formData.examPeriodType === 'SINGLE' ? 'Période d\'examens' : 'Examens Semestre 1'}
            </h4>
            {formData.examPeriodType === 'PER_SEMESTER' && semesterDates.semester1End && (
              <p className="text-xs text-gray-500 mb-3">
                Doit se terminer avant le {formatDate(semesterDates.semester1End)}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début
                </label>
                {!isEditing ? (
                  <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {formData.examPeriod1Start ? formatDate(formData.examPeriod1Start) : 'Non définie'}
                  </div>
                ) : (
                  <>
                    <input
                      type="date"
                      value={formData.examPeriod1Start}
                      onChange={(e) => updateField('examPeriod1Start', e.target.value)}
                      min={formData.examPeriodType === 'PER_SEMESTER' ? getMinDateForField('examPeriod1Start') : undefined}
                      max={formData.examPeriodType === 'PER_SEMESTER' ? getMaxDateForField('examPeriod1Start') : undefined}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                        formData.examPeriodType === 'PER_SEMESTER' && getDateValidation('examPeriod1Start', formData.examPeriod1Start)
                          ? 'border-red-400 bg-red-50'
                          : 'border-blue-300'
                      }`}
                    />
                    {formData.examPeriodType === 'PER_SEMESTER' && getDateValidation('examPeriod1Start', formData.examPeriod1Start) && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {getDateValidation('examPeriod1Start', formData.examPeriod1Start)}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin
                </label>
                {!isEditing ? (
                  <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {formData.examPeriod1End ? formatDate(formData.examPeriod1End) : 'Non définie'}
                  </div>
                ) : (
                  <>
                    <input
                      type="date"
                      value={formData.examPeriod1End}
                      onChange={(e) => updateField('examPeriod1End', e.target.value)}
                      min={formData.examPeriodType === 'PER_SEMESTER' ? getMinDateForField('examPeriod1End') : undefined}
                      max={formData.examPeriodType === 'PER_SEMESTER' ? getMaxDateForField('examPeriod1End') : undefined}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                        formData.examPeriodType === 'PER_SEMESTER' && getDateValidation('examPeriod1End', formData.examPeriod1End)
                          ? 'border-red-400 bg-red-50'
                          : 'border-blue-300'
                      }`}
                    />
                    {formData.examPeriodType === 'PER_SEMESTER' && getDateValidation('examPeriod1End', formData.examPeriod1End) && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {getDateValidation('examPeriod1End', formData.examPeriod1End)}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Period 2 (only for PER_SEMESTER) */}
          {formData.examPeriodType === 'PER_SEMESTER' && (
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-green-800 mb-3">Examens Semestre 2</h4>
              {semesterDates.semester2End && (
                <p className="text-xs text-gray-500 mb-3">
                  Doit se terminer avant le {formatDate(semesterDates.semester2End)}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de début
                  </label>
                  {!isEditing ? (
                    <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {formData.examPeriod2Start ? formatDate(formData.examPeriod2Start) : 'Non définie'}
                    </div>
                  ) : (
                    <>
                      <input
                        type="date"
                        value={formData.examPeriod2Start}
                        onChange={(e) => updateField('examPeriod2Start', e.target.value)}
                        min={getMinDateForField('examPeriod2Start')}
                        max={getMaxDateForField('examPeriod2Start')}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white ${
                          getDateValidation('examPeriod2Start', formData.examPeriod2Start)
                            ? 'border-red-400 bg-red-50'
                            : 'border-green-300'
                        }`}
                      />
                      {getDateValidation('examPeriod2Start', formData.examPeriod2Start) && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {getDateValidation('examPeriod2Start', formData.examPeriod2Start)}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de fin
                  </label>
                  {!isEditing ? (
                    <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {formData.examPeriod2End ? formatDate(formData.examPeriod2End) : 'Non définie'}
                    </div>
                  ) : (
                    <>
                      <input
                        type="date"
                        value={formData.examPeriod2End}
                        onChange={(e) => updateField('examPeriod2End', e.target.value)}
                        min={getMinDateForField('examPeriod2End')}
                        max={getMaxDateForField('examPeriod2End')}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white ${
                          getDateValidation('examPeriod2End', formData.examPeriod2End)
                            ? 'border-red-400 bg-red-50'
                            : 'border-green-300'
                        }`}
                      />
                      {getDateValidation('examPeriod2End', formData.examPeriod2End) && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {getDateValidation('examPeriod2End', formData.examPeriod2End)}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-sm text-blue-600 mt-4 flex items-center gap-2">
          <Info size={16} />
          Ces périodes seront affichées comme événements académiques dans le calendrier annuel.
        </p>
      </div>

      {/* Rattrapage Toggle - Right after exam periods */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="text-amber-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Activer les rattrapages</h3>
              <p className="text-sm text-gray-500">
                Permet de planifier des examens de rattrapage pour les étudiants en échec
              </p>
            </div>
          </div>
          {!isEditing ? (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              formData.rattrapageEnabled
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {formData.rattrapageEnabled ? 'Activé' : 'Désactivé'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => updateField('rattrapageEnabled', !formData.rattrapageEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.rattrapageEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.rattrapageEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          )}
        </div>

        {/* Rattrapage Period Configuration - Only shown if enabled */}
        {formData.rattrapageEnabled && (
          <div className="mt-6 pt-6 border-t border-amber-200">
            <h4 className="font-medium text-amber-900 mb-4">Configuration des périodes de rattrapage</h4>

            {/* Rattrapage Period Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Combien de périodes de rattrapage avez-vous ?
              </label>
              {!isEditing ? (
                <div className="flex items-center gap-2">
                  <span className={`px-4 py-2 rounded-lg font-medium ${
                    formData.rattrapagePeriodType === 'SINGLE'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {formData.rattrapagePeriodType === 'SINGLE'
                      ? 'Une seule période (fin d\'année)'
                      : 'Deux périodes (une par semestre)'
                    }
                  </span>
                </div>
              ) : (
                <div className="flex gap-4">
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                    formData.rattrapagePeriodType === 'SINGLE'
                      ? 'bg-amber-100 border-2 border-amber-400'
                      : 'bg-white border-2 border-gray-200 hover:border-amber-200'
                  }`}>
                    <input
                      type="radio"
                      name="rattrapagePeriodType"
                      checked={formData.rattrapagePeriodType === 'SINGLE'}
                      onChange={() => updateField('rattrapagePeriodType', 'SINGLE')}
                      className="sr-only"
                    />
                    <div>
                      <p className={`font-medium ${formData.rattrapagePeriodType === 'SINGLE' ? 'text-amber-700' : 'text-gray-700'}`}>
                        Une seule période
                      </p>
                      <p className="text-xs text-gray-500">Rattrapage en fin d'année</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                    formData.rattrapagePeriodType === 'PER_SEMESTER'
                      ? 'bg-amber-100 border-2 border-amber-400'
                      : 'bg-white border-2 border-gray-200 hover:border-amber-200'
                  }`}>
                    <input
                      type="radio"
                      name="rattrapagePeriodType"
                      checked={formData.rattrapagePeriodType === 'PER_SEMESTER'}
                      onChange={() => updateField('rattrapagePeriodType', 'PER_SEMESTER')}
                      className="sr-only"
                    />
                    <div>
                      <p className={`font-medium ${formData.rattrapagePeriodType === 'PER_SEMESTER' ? 'text-amber-700' : 'text-gray-700'}`}>
                        Par semestre
                      </p>
                      <p className="text-xs text-gray-500">Une période de rattrapage par semestre</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Rattrapage Period Dates */}
            <div className="space-y-4">
              {/* Rattrapage Period 1 */}
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <h5 className="font-medium text-amber-800 mb-3">
                  {formData.rattrapagePeriodType === 'SINGLE' ? 'Période de rattrapage' : 'Rattrapage Semestre 1'}
                </h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de début
                    </label>
                    {!isEditing ? (
                      <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                        {formData.rattrapagePeriod1Start ? formatDate(formData.rattrapagePeriod1Start) : 'Non définie'}
                      </div>
                    ) : (
                      <input
                        type="date"
                        value={formData.rattrapagePeriod1Start}
                        onChange={(e) => updateField('rattrapagePeriod1Start', e.target.value)}
                        className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de fin
                    </label>
                    {!isEditing ? (
                      <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                        {formData.rattrapagePeriod1End ? formatDate(formData.rattrapagePeriod1End) : 'Non définie'}
                      </div>
                    ) : (
                      <input
                        type="date"
                        value={formData.rattrapagePeriod1End}
                        onChange={(e) => updateField('rattrapagePeriod1End', e.target.value)}
                        className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Rattrapage Period 2 (only for PER_SEMESTER) */}
              {formData.rattrapagePeriodType === 'PER_SEMESTER' && (
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <h5 className="font-medium text-orange-800 mb-3">Rattrapage Semestre 2</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date de début
                      </label>
                      {!isEditing ? (
                        <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                          {formData.rattrapagePeriod2Start ? formatDate(formData.rattrapagePeriod2Start) : 'Non définie'}
                        </div>
                      ) : (
                        <input
                          type="date"
                          value={formData.rattrapagePeriod2Start}
                          onChange={(e) => updateField('rattrapagePeriod2Start', e.target.value)}
                          className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date de fin
                      </label>
                      {!isEditing ? (
                        <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                          {formData.rattrapagePeriod2End ? formatDate(formData.rattrapagePeriod2End) : 'Non définie'}
                        </div>
                      ) : (
                        <input
                          type="date"
                          value={formData.rattrapagePeriod2End}
                          onChange={(e) => updateField('rattrapagePeriod2End', e.target.value)}
                          className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm text-amber-600 mt-4 flex items-center gap-2">
              <Info size={16} />
              Ces périodes seront affichées comme événements académiques dans le calendrier annuel.
            </p>
          </div>
        )}
      </div>

      {/* Scheduling Rules */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="text-gray-500" size={20} />
          Règles de planification
        </h3>

        <div className="space-y-6">
          {/* Max exams per day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre maximum d'examens par jour et par groupe
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-900 w-24">
                {formData.maxExamsPerDayPerGroup}
              </div>
            ) : (
              <select
                value={formData.maxExamsPerDayPerGroup}
                onChange={(e) => updateField('maxExamsPerDayPerGroup', parseInt(e.target.value))}
                className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[1, 2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Un avertissement sera affiché si ce nombre est dépassé
            </p>
          </div>

          {/* Min days between exams */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre minimum de jours entre les examens (par groupe)
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-900 w-24">
                {formData.minDaysBetweenExams}
              </div>
            ) : (
              <select
                value={formData.minDaysBetweenExams}
                onChange={(e) => updateField('minDaysBetweenExams', parseInt(e.target.value))}
                className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[0, 1, 2, 3, 4, 5, 6, 7].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              0 = pas de restriction sur l'espacement
            </p>
          </div>

          {/* Toggle options */}
          <div className="space-y-4">
            {/* Enforce exam period */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Appliquer la période d'examens</p>
                <p className="text-sm text-gray-500">
                  Afficher un avertissement si un examen est planifié en dehors de la période d'examens
                </p>
              </div>
              {!isEditing ? (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  formData.enforceExamPeriod
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {formData.enforceExamPeriod ? 'Activé' : 'Désactivé'}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => updateField('enforceExamPeriod', !formData.enforceExamPeriod)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.enforceExamPeriod ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.enforceExamPeriod ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              )}
            </div>

            {/* Allow weekend exams */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Autoriser les examens le weekend</p>
                <p className="text-sm text-gray-500">
                  Permet de planifier des examens le samedi et le dimanche
                </p>
              </div>
              {!isEditing ? (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  formData.allowWeekendExams
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {formData.allowWeekendExams ? 'Activé' : 'Désactivé'}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => updateField('allowWeekendExams', !formData.allowWeekendExams)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.allowWeekendExams ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.allowWeekendExams ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
